
/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Persisted accessibility preferences live alongside the rest of the
// SoftUI controller so any component can read or update them with
// useSoftUIController(). They survive page refresh via localStorage.
const A11Y_STORAGE_KEY = "tiffin-admin-a11y";
const FONT_SCALE_OPTIONS = [
  { id: "normal", label: "Normal", value: 1 },
  { id: "large", label: "Large", value: 1.15 },
  { id: "xlarge", label: "Extra large", value: 1.3 },
];
const THEME_MODE_OPTIONS = [
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
  { id: "highContrast", label: "High contrast" },
];

function loadA11yPrefs() {
  if (typeof window === "undefined") return { fontScale: 1, themeMode: "light" };
  try {
    const raw = window.localStorage?.getItem(A11Y_STORAGE_KEY);
    if (!raw) return { fontScale: 1, themeMode: "light" };
    const parsed = JSON.parse(raw);
    const scale = Number(parsed?.fontScale);
    const mode = parsed?.themeMode;
    return {
      fontScale: Number.isFinite(scale) && scale > 0 ? scale : 1,
      themeMode: ["light", "dark", "highContrast"].includes(mode) ? mode : "light",
    };
  } catch (_e) {
    return { fontScale: 1, themeMode: "light" };
  }
}

// The Soft UI Dashboard PRO Material main context
const SoftUI = createContext(null);

// Setting custom name for the context which is visible on react dev tools
SoftUI.displayName = "SoftUIContext";

// Soft UI Dashboard React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "FONT_SCALE": {
      return { ...state, fontScale: action.value };
    }
    case "THEME_MODE": {
      return { ...state, themeMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Soft UI Dashboard React context provider
function SoftUIControllerProvider({ children }) {
  const persisted = loadA11yPrefs();
  const initialState = {
    miniSidenav: false,
    transparentSidenav: true,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    fontScale: persisted.fontScale,
    themeMode: persisted.themeMode,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  // Persist accessibility prefs and apply them to the document root so any
  // component using rem-based sizing or the html[data-theme] selector picks
  // them up automatically.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage?.setItem(
        A11Y_STORAGE_KEY,
        JSON.stringify({ fontScale: controller.fontScale, themeMode: controller.themeMode })
      );
    } catch (_e) {
      // ignore quota / private mode errors
    }
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.style.fontSize = `${Math.round(controller.fontScale * 16)}px`;
      document.documentElement.dataset.theme = controller.themeMode;
    }
  }, [controller.fontScale, controller.themeMode]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <SoftUI.Provider value={value}>{children}</SoftUI.Provider>;
}

// Soft UI Dashboard React custom hook for using context
function useSoftUIController() {
  const context = useContext(SoftUI);

  if (!context) {
    throw new Error("useSoftUIController should be used inside the SoftUIControllerProvider.");
  }

  return context;
}

// Typechecking props for the SoftUIControllerProvider
SoftUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setFontScale = (dispatch, value) => dispatch({ type: "FONT_SCALE", value });
const setThemeMode = (dispatch, value) => dispatch({ type: "THEME_MODE", value });

export {
  SoftUIControllerProvider,
  useSoftUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setFontScale,
  setThemeMode,
  FONT_SCALE_OPTIONS,
  THEME_MODE_OPTIONS,
};
